import { brandLogoUrl, brands, brandsData, getBrand } from "@/lib/brands";
import { productImageUrl } from "@/lib/productImageUrl";
import staticPagesData from "@/data/staticPages.json";
import type { Brand } from "@/lib/brands";
import type { PerfumeProduct } from "@/services/catalogService";
import type { StaticPage } from "@/types/staticPages";

export const SITE_NAME = "Axiom Manifold";
export const SITE_ORIGIN = "https://axiommanifold.com";
export const DEFAULT_SOCIAL_IMAGE = `${SITE_ORIGIN}/og.png`;

export interface SeoMetadata {
  canonicalUrl: string;
  description: string;
  imageUrl?: string;
  indexable: boolean;
  jsonLd: Record<string, unknown>[];
  title: string;
  type: "website" | "article" | "product";
}

const staticPages = staticPagesData as StaticPage[];

const PRIVATE_ROUTE_PREFIXES = [
  "/admin",
  "/auth",
  "/cart",
  "/checkout",
  "/login",
  "/mail",
  "/profile",
  "/signup",
  "/unsubscribe",
];

const STATIC_ROUTE_COPY: Record<string, { description: string; title: string }> = {
  "/": {
    title: "Rare & Vintage Fragrance Boutique | Axiom Manifold",
    description: "Explore rare, vintage and discontinued fragrances, decants and collector guides from Axiom Manifold in Melbourne, Australia.",
  },
  "/brands": {
    title: "Fragrance Houses & Perfume Brands | Axiom Manifold",
    description: brandsData.page.description,
  },
  "/catalog": {
    title: "Rare, Vintage & Discontinued Perfume Catalog | Axiom Manifold",
    description: "Browse the Axiom Manifold catalog of rare, vintage, discontinued and contemporary fragrances, samples and decants with condition and availability details.",
  },
  "/contact": {
    title: "Contact Axiom Manifold | Fragrance Enquiries",
    description: "Contact Axiom Manifold about fragrances, orders, shipping, product details or collector guidance.",
  },
  "/faq": {
    title: "Vintage Fragrance FAQs | Axiom Manifold",
    description: "Answers about fragrance authenticity, vintage formulations, storage, shipping and returns at Axiom Manifold.",
  },
  "/flow": {
    title: "Fragrance Discovery Flow | Axiom Manifold",
    description: "Use Axiom Manifold's guided fragrance flow to move from broad preferences toward useful perfume directions.",
  },
  "/guidance": {
    title: "Fragrance Guidance | Axiom Manifold",
    description: "Practical guidance for exploring fragrance families, concentrations, vintage bottles, samples and decants.",
  },
};

function decodeEntities(value: string): string {
  const entities: Record<string, string> = {
    "&amp;": "&",
    "&apos;": "'",
    "&#39;": "'",
    "&gt;": ">",
    "&lt;": "<",
    "&nbsp;": " ",
    "&quot;": '"',
  };
  return value.replace(/&(amp|apos|#39|gt|lt|nbsp|quot);/gi, (entity) => entities[entity.toLowerCase()] || entity);
}

export function plainText(value?: string | null): string {
  return decodeEntities(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value: string, maximum = 158): string {
  const normalized = plainText(value);
  if (normalized.length <= maximum) return normalized;
  const shortened = normalized.slice(0, maximum - 1).replace(/\s+\S*$/, "").trim();
  return `${shortened || normalized.slice(0, maximum - 1).trim()}…`;
}

function canonical(pathname: string): string {
  return pathname === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${pathname}`;
}

function organizationJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_ORIGIN}/#organization`,
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
    logo: `${SITE_ORIGIN}/android-chrome-512x512.png`,
    description: "A Melbourne fragrance boutique and collector resource for rare, vintage and discontinued perfume.",
  };
}

function webSiteJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
    description: STATIC_ROUTE_COPY["/"].description,
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    inLanguage: "en-AU",
  };
}

function breadcrumbJsonLd(items: Array<{ name: string; path: string }>): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonical(item.path),
    })),
  };
}

function baseMetadata(
  pathname: string,
  copy: { description: string; title: string },
  options: Partial<Pick<SeoMetadata, "imageUrl" | "indexable" | "jsonLd" | "type">> = {},
): SeoMetadata {
  return {
    canonicalUrl: canonical(pathname),
    description: truncateText(copy.description),
    imageUrl: options.imageUrl,
    indexable: options.indexable ?? true,
    jsonLd: options.jsonLd || [],
    title: plainText(copy.title),
    type: options.type || "website",
  };
}

export function staticRouteSeo(pathname: string): SeoMetadata | null {
  const copy = STATIC_ROUTE_COPY[pathname];
  if (copy) {
    const jsonLd: Record<string, unknown>[] = pathname === "/"
      ? [organizationJsonLd(), webSiteJsonLd()]
      : [breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: copy.title.split("|")[0].trim(), path: pathname },
        ])];
    return baseMetadata(pathname, copy, {
      imageUrl: DEFAULT_SOCIAL_IMAGE,
      jsonLd,
      type: "website",
    });
  }

  if (pathname.startsWith("/brands/")) {
    const brand = getBrand(pathname.slice("/brands/".length));
    return brand ? brandSeo(brand) : null;
  }

  if (pathname.startsWith("/info/")) {
    const page = staticPages.find((candidate) => candidate.slug === pathname.slice("/info/".length));
    return page ? staticPageSeo(page) : null;
  }

  if (PRIVATE_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return privateRouteSeo(pathname);
  }

  return null;
}

export function brandSeo(brand: Brand): SeoMetadata {
  const pathname = `/brands/${brand.slug}`;
  const description = `${brand.description} Explore ${brand.name} fragrances in the Axiom Manifold archive.`;
  return baseMetadata(pathname, {
    title: `${brand.name} Fragrances & House Profile | ${SITE_NAME}`,
    description,
  }, {
    imageUrl: brandLogoUrl(brand),
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Fragrance houses", path: "/brands" },
        { name: brand.name, path: pathname },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${brand.name} at ${SITE_NAME}`,
        url: canonical(pathname),
        description: truncateText(description, 300),
        isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
      },
    ],
    type: "article",
  });
}

export function staticPageSeo(page: StaticPage): SeoMetadata {
  const pathname = page.slug === "faq" ? "/faq" : `/info/${page.slug}`;
  const description = page.description || page.summary || page.subtitle || `Read ${page.title} from Axiom Manifold.`;
  const breadcrumbs = [{ name: "Home", path: "/" }];
  if (page.parentId) breadcrumbs.push({ name: "Collector Guides", path: `/info/${page.parentId}` });
  breadcrumbs.push({ name: page.title, path: pathname });

  return baseMetadata(pathname, {
    title: `${page.title} | ${SITE_NAME}`,
    description,
  }, {
    jsonLd: [
      breadcrumbJsonLd(breadcrumbs),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: plainText(page.title),
        description: truncateText(description, 300),
        author: { "@id": `${SITE_ORIGIN}/#organization` },
        publisher: { "@id": `${SITE_ORIGIN}/#organization` },
        mainEntityOfPage: canonical(pathname),
        inLanguage: "en-AU",
      },
    ],
    type: "article",
  });
}

function productDescription(product: PerfumeProduct): string {
  const source = product.shortDescription || product.description;
  if (plainText(source)) return truncateText(source);
  return truncateText(`${product.name} by ${product.brand}. View presentation, condition, availability and Australian pricing at ${SITE_NAME}.`);
}

function productOffer(product: PerfumeProduct): Record<string, unknown> {
  if ("aggregate" in product) {
    const variants = (product as PerfumeProduct & { aggregate: { variants: Array<{ inStock: boolean; price: number; stock: number }> } }).aggregate.variants;
    const prices = variants.map((variant) => variant.price);
    return {
      "@type": "AggregateOffer",
      priceCurrency: "AUD",
      lowPrice: Math.min(...prices).toFixed(2),
      highPrice: Math.max(...prices).toFixed(2),
      offerCount: variants.length,
      availability: variants.some((variant) => variant.inStock && variant.stock > 0)
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      url: canonical(`/product/${encodeURIComponent(product.sku)}`),
    };
  }

  return {
    "@type": "Offer",
    priceCurrency: "AUD",
    price: Number(product.price).toFixed(2),
    availability: (product.inStock ?? product.stock > 0) && product.stock > 0
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    url: canonical(`/product/${encodeURIComponent(product.sku)}`),
    seller: { "@id": `${SITE_ORIGIN}/#organization` },
  };
}

export function productSeo(product: PerfumeProduct): SeoMetadata {
  const pathname = `/product/${encodeURIComponent(product.sku)}`;
  const cleanName = plainText(product.name);
  const cleanBrand = plainText(product.brand);
  const titlePrefix = cleanName.toLocaleLowerCase().includes(cleanBrand.toLocaleLowerCase())
    ? cleanName
    : `${cleanName} by ${cleanBrand}`;
  const description = productDescription(product);
  const imageUrl = productImageUrl(product.images.main);
  const condition = plainText(String(product.additionalInfo.condition || ""));
  const productJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${canonical(pathname)}#product`,
    name: cleanName,
    description: truncateText(description, 500),
    sku: plainText(product.sku),
    brand: { "@type": "Brand", name: cleanBrand },
    category: "Fragrance",
    image: [imageUrl],
    url: canonical(pathname),
    offers: productOffer(product),
  };
  if (/\bnew\b/i.test(condition)) productJsonLd.itemCondition = "https://schema.org/NewCondition";
  else if (condition && !/unknown/i.test(condition)) productJsonLd.itemCondition = "https://schema.org/UsedCondition";

  return baseMetadata(pathname, {
    title: `${truncateText(titlePrefix, 62)} | ${SITE_NAME}`,
    description,
  }, {
    imageUrl,
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Catalog", path: "/catalog" },
        { name: cleanName, path: pathname },
      ]),
      productJsonLd,
    ],
    type: "product",
  });
}

export function productQuerySeo(field: string, term: string, total: number): SeoMetadata {
  const params = new URLSearchParams({ [field]: term });
  const pathname = `/product?${params.toString()}`;
  return baseMetadata(pathname, {
    title: `Product lookup results | ${SITE_NAME}`,
    description: total === 1
      ? `One product matched the supplied ${field} query.`
      : `${total} products matched the supplied ${field} query.`,
  }, {
    indexable: false,
    jsonLd: [],
  });
}

export function privateRouteSeo(pathname: string): SeoMetadata {
  return baseMetadata(pathname, {
    title: `Private or transactional page | ${SITE_NAME}`,
    description: "This account or transactional page is not intended for search results.",
  }, {
    indexable: false,
    jsonLd: [],
  });
}

export function notFoundSeo(pathname: string, gone = false): SeoMetadata {
  return baseMetadata(pathname, {
    title: `${gone ? "Resource no longer available" : "Page not found"} | ${SITE_NAME}`,
    description: gone
      ? "This former Axiom Manifold resource is no longer available."
      : "The requested Axiom Manifold page could not be found.",
  }, {
    indexable: false,
    jsonLd: [],
  });
}

export function allKnownBrands(): Brand[] {
  return brands;
}
